/* =============================================================================
   3 Reasons Why You May Have a Thyroid Deficit — combined page interactions
   - subscribe & save toggle (desktop + mobile) — no-ops if the price markup
     isn't present (this page's CTA is a learn-more box without a price row)
   - nav scroll state
   - reveal-on-scroll via IntersectionObserver (hero reveals immediately)
   - hero heading fit-to-one-line (desktop)
   - sticky CTA visibility (desktop bar/float + mobile bottom bar)
   - Shopify theme editor re-initialization
   ============================================================================= */
(function () {
  'use strict';

  /* ---- Subscribe toggle ---- */
  function wireToggle(toggleId) {
    var toggle = document.getElementById(toggleId);
    if (!toggle) return;
    var box = toggle.closest('.buybox--desktop, .buybox-mob');
    if (!box) return;
    var price = box.querySelector('.buybox-price');
    var was = box.querySelector('.buybox-was');
    var save = box.querySelector('.buybox-save');
    var label = box.querySelector('.sub-label');
    toggle.addEventListener('change', function () {
      if (toggle.checked) {
        if (price) price.textContent = '$189';
        if (was) { was.textContent = '$289'; was.style.display = ''; }
        if (save) { save.textContent = 'Save $100'; save.style.display = ''; }
        if (label) label.textContent = 'Subscribe & Save · every 60 days';
      } else {
        if (price) price.textContent = '$289';
        if (was) was.style.display = 'none';
        if (save) save.style.display = 'none';
        if (label) label.textContent = 'One-Time Purchase';
      }
    });
  }
  wireToggle('subToggleDesktop');
  wireToggle('subToggleMobile');

  /* ---- Nav scroll state ---- */
  var nav = document.getElementById('siteNav');
  if (nav) {
    window.addEventListener('scroll', function () {
      nav.classList.toggle('scrolled', window.scrollY > 60);
    }, { passive: true });
  }

  /* ---- Reveal on scroll ---- */
  /* Guard the observer: if IntersectionObserver is unavailable (older browsers
     / some embedded webviews), its constructor throws. Since every .reveal
     starts at opacity:0 in CSS, a throw here would leave the whole page blank
     and skip the sticky-CTA / rewards-hide code below. Fall back to revealing
     immediately when there's no observer. */
  var io = null;
  if ('IntersectionObserver' in window) {
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.10, rootMargin: '0px 0px -36px 0px' });
  }

  function initReveals(root) {
    var reveals = (root || document).querySelectorAll('.reveal:not(.in)');
    Array.prototype.forEach.call(reveals, function (el) {
      if (!io) {
        /* No observer support — reveal immediately so content is never hidden. */
        el.classList.add('in');
      } else if (el.closest('.hero')) {
        /* Hero items — fire with CSS delay, no observer needed */
        var delay = parseFloat(getComputedStyle(el).getPropertyValue('--d') || '0') * 1000;
        setTimeout(function () { el.classList.add('in'); }, 80 + delay);
      } else {
        io.observe(el);
      }
    });
  }
  initReveals();

  /* ---- Hero heading: fit to one line at the largest size that fits (desktop) ----
     The intrinsic one-line text width is measured with the heading set to
     inline-block (so it shrinks to its content), then the font-size is scaled
     so the text fills the available column width without wrapping. On mobile
     (<=920px) the CSS-defined size and natural wrapping are restored. */
  function fitHeroHeading() {
    var h1 = document.querySelector('.hero h1');
    if (!h1) return;
    // Reset any previously applied inline sizing before measuring.
    h1.style.fontSize = '';
    h1.style.whiteSpace = '';
    h1.style.display = '';
    if (window.innerWidth <= 920) return;

    var parent = h1.parentElement;
    if (!parent) return;
    var pcs = getComputedStyle(parent);
    var avail = parent.clientWidth -
      (parseFloat(pcs.paddingLeft) || 0) - (parseFloat(pcs.paddingRight) || 0);
    if (!avail) return;

    // Measure the intrinsic single-line width at the current CSS size.
    h1.style.whiteSpace = 'nowrap';
    h1.style.display = 'inline-block';
    var current = parseFloat(getComputedStyle(h1).fontSize) || 56;
    var textW = h1.getBoundingClientRect().width;
    h1.style.display = '';
    if (!textW) { h1.style.whiteSpace = ''; return; }

    var target = current * (avail / textW) * 0.99; // a hair of breathing room
    target = Math.max(32, Math.min(target, 120));  // sane bounds
    h1.style.fontSize = target + 'px';
    h1.style.whiteSpace = 'nowrap';
  }
  fitHeroHeading();
  // Re-fit once the custom serif font loads (its metrics differ from the fallback).
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(fitHeroHeading);
  }
  var fitRaf;
  window.addEventListener('resize', function () {
    if (fitRaf) cancelAnimationFrame(fitRaf);
    fitRaf = requestAnimationFrame(fitHeroHeading);
  }, { passive: true });

  /* ---- Sticky CTA visibility (ported from the System Roadmap) ----
     Reveals the sticky chrome once the hero (id="top") has scrolled off, and
     hides it again near the buy box (id="start") so it never overlaps the CTA. */
  function initStickyCta() {
    var hero = document.getElementById('top');
    var stickyBar = document.getElementById('stickyBar');
    var stickyBtn = document.getElementById('stickyBtnFloat');
    var stickyMob = document.getElementById('stickyMobile');
    var heroCta = document.getElementById('heroCtaFloat');
    var buyBox = document.getElementById('start');
    if (!hero || (!stickyBar && !stickyBtn && !stickyMob && !heroCta)) return;

    function updateStickyVisibility() {
      var heroRect = hero.getBoundingClientRect();
      var headerOffset = 120;
      var hdr = document.querySelector(
        'header.header, .header-wrapper, .section-header, #shopify-section-header');
      if (hdr) {
        var h = hdr.getBoundingClientRect().height;
        if (h) headerOffset = h;
      }
      var pastHero = heroRect.bottom < headerOffset;
      var headerGone = heroRect.top <= 0;
      var atBuyBox = buyBox
        ? buyBox.getBoundingClientRect().top < window.innerHeight * 0.5
        : false;
      var shouldShow = pastHero && !atBuyBox;

      if (stickyBar) {
        stickyBar.classList.toggle('visible', shouldShow);
        stickyBar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      }
      if (stickyMob) {
        stickyMob.classList.toggle('visible', shouldShow);
        stickyMob.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      }
      if (stickyBtn) {
        stickyBtn.classList.toggle('visible', shouldShow);
        stickyBtn.classList.toggle('in-bar', shouldShow);
      }
      if (heroCta) {
        heroCta.classList.toggle('visible', headerGone && !pastHero && !atBuyBox);
      }
    }

    window.addEventListener('scroll', updateStickyVisibility, { passive: true });
    window.addEventListener('resize', updateStickyVisibility, { passive: true });
    updateStickyVisibility();
  }
  initStickyCta();

  /* ---- Hide the Stamped rewards widget (this page only) ----
     The widget is injected asynchronously by the Stamped app. The page-scoped
     CSS rule (display:none !important) is the primary fix and beats the
     widget's inline display:block; this observer also force-hides it the
     moment it's injected so there's no flash. This script is loaded only by
     the 3 Reasons hero section, so it never runs on other pages. */
  (function () {
    var SEL = '#stamped-rewards-init,.stamped-rewards-init,' +
              '.stamped-rewards-launcher,.stamped-rewards-base';
    function hideRewards(root) {
      var nodes;
      try { nodes = (root || document).querySelectorAll(SEL); }
      catch (e) { return; }
      Array.prototype.forEach.call(nodes, function (el) {
        el.style.setProperty('display', 'none', 'important');
      });
    }
    hideRewards();
    if (window.MutationObserver) {
      var scheduled = false;
      var obs = new MutationObserver(function () {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(function () { scheduled = false; hideRewards(); });
      });
      obs.observe(document.documentElement, { childList: true, subtree: true });
    }
  })();

  /* ---- Shopify Theme Editor: re-initialize on section load/select ----
     The editor swaps in fresh DOM after a setting change, so the original
     IntersectionObserver is no longer watching those nodes. Reveal them
     immediately (scroll animation is distracting in the editor). */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      event.target.querySelectorAll('.reveal').forEach(function (el) {
        el.classList.add('in');
      });
      wireToggle('subToggleDesktop');
      wireToggle('subToggleMobile');
      fitHeroHeading();
      initStickyCta();
    });
    document.addEventListener('shopify:section:select', function (event) {
      event.target.querySelectorAll('.reveal').forEach(function (el) {
        el.classList.add('in');
      });
      fitHeroHeading();
    });
    document.addEventListener('shopify:block:select', function (event) {
      if (event.target.classList.contains('reveal')) {
        event.target.classList.add('in');
      }
      event.target.querySelectorAll('.reveal').forEach(function (el) {
        el.classList.add('in');
      });
    });
  }

})();
